// eslint-disable-next-line import/prefer-default-export
import _ from 'lodash';

/**
 * Whether there is a product to open an order for. The atom holds a plain catalog product, and
 * nothing else counts — not null, not the empty default, not a class instance.
 *
 * @param {object} product
 * @returns {boolean}
 */
export const isPurchasable = (product) => !!product
  && Object.keys(product).length > 0
  && Object.getPrototypeOf(product) === Object.prototype;

export const convertToPurchasedProduct = (product) => {
  if (!product) {
    return null;
  }

  const {
    productId,
    exchange: {
      price,
      currency,
    },
    awards = [],
  } = product;

  const purchasedAwards = awards.map((award) => ({
    amount: award.amount,
    currency: award.currency,
  }));

  return {
    productId,
    exchange: {
      price,
      currency,
    },
    awards: purchasedAwards,
  };
};

export function isBundle(product) {
  return product && (product.categories.some((c) => c.includes('bundle') || product.productSku.startsWith('bundle:')));
}

export function filterByInventory(products, inventory, limit = 0, limited = false) {
  if (!products) {
    return [];
  }

  const availableProducts = products.filter(
    (product) => !product.requiresAny
      || product.requiresAny.length < 1
      || product.requiresAny.some(
        (sku) => inventory.find((item) => item.productSku === sku && item.amount > 0),
      ),
  )
    .filter((product) => product.categories.some((c) => c.includes('limited')) === limited);

  const groupedProducts = _.groupBy(availableProducts, (product) => product.purchase.price);
  return _.map(
    groupedProducts,
    (group) => _.maxBy(group, (product) => product.exchange.price),
  ).slice(limit * -1);
}

/**
 * Products awarding consumable items cannot be de-duplicated by purchase price the way
 * currency conversions are: two different items may share a Diamond price, and their
 * `exchange.price` is a quantity of a different currency rather than a comparable rate.
 *
 * @param {Array<object>} products
 * @param {Array<object>} inventory
 * @param {boolean} [limited]
 * @returns {Array<object>}
 */
export function filterItemConvertByInventory(products, inventory, limited = false) {
  if (!products) {
    return [];
  }

  return products.filter(
    (product) => !product.requiresAny
      || product.requiresAny.length < 1
      || product.requiresAny.some(
        (sku) => (inventory || []).find((item) => item.productSku === sku && item.amount > 0),
      ),
  )
    .filter((product) => product.categories.some((c) => c.includes('limited')) === limited);
}

/**
 * The display name for a product. Catalog products carry their localized name in
 * `details.name`, but an unresolved `#i18n:` token is returned when no translation is
 * available, in which case the untranslated product name is the better fallback.
 *
 * @param {object} product
 * @returns {string}
 */
export function getProductDisplayName(product) {
  const { name, details: { name: detailsName } = {} } = product || {};

  if (detailsName && !detailsName.startsWith('#i18n:')) {
    return detailsName;
  }

  return name || '';
}

/* The currency prefix marking an award as a consumable item rather than app currency. Same
 * pattern web-payments filters consumable charges on (`catalog.features.*.consumablePattern`).
 */
const CONSUMABLE_CURRENCY_PREFIX = 'consumable:';

/**
 * The number of items a product awards. A product may award several copies of its item, so the
 * amount has to be shown alongside the item name.
 *
 * Read off the `consumable:` entry of `awards` rather than `exchange.price`: `exchange` only
 * ever carries one currency, so a product awarding something besides the item could report the
 * wrong quantity there. Falls back to 1, which renders as a bare item name with no quantity.
 *
 * @param {object} product
 * @returns {number}
 */
export function getProductAwardAmount(product) {
  const { awards = [] } = product || {};

  const { amount } = awards.find(
    (award) => (award?.currency || '').startsWith(CONSUMABLE_CURRENCY_PREFIX),
  ) || {};

  return amount || 1;
}

export function summarize(charges, currency = 'DMD') {
  const total = charges.reduce((sum, charge) => {
    if ((charge.currency || '').toUpperCase() !== currency) {
      return sum;
    }

    return sum + (Number(charge.amount) || 0);
  }, 0);

  return total;
}

/**
 * An amount expressed in the currency's minor units, which is what payment sheets take.
 *
 * The number of decimals comes from `Intl` rather than a table we would have to maintain:
 * it is 2 for USD and EUR, 0 for JPY and KRW, 3 for BHD.
 *
 * @param {number} amount in major units
 * @param {string} currency ISO code
 * @returns {(number|undefined)} amount in minor units
 */
export function toMinorUnits(amount, currency) {
  if (amount === undefined || amount === null) {
    return undefined;
  }

  const { maximumFractionDigits: digits } = new Intl.NumberFormat('en', {
    style: 'currency',
    currency,
  }).resolvedOptions();

  return Math.round(amount * 10 ** digits);
}
